'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { focusRing, useDensity } from '@liftango/ui-tokens';

export type ButtonKind = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Visual treatment. Defaults to `secondary`. */
  kind?: ButtonKind;
  /** Height: sm = 24px, md = 28px, lg = 34px (default). */
  size?: ButtonSize;
  /** Optional leading icon node. */
  icon?: React.ReactNode;
  /** HTML button type. Defaults to `button` to avoid accidental form submits. */
  type?: 'button' | 'submit' | 'reset';
}

const SIZE_HEIGHT: Record<ButtonSize, string> = { sm: '24px', md: '28px', lg: '34px' };
const SIZE_PADDING_X: Record<ButtonSize, string> = { sm: '10px', md: '12px', lg: '14px' };
const SIZE_FONT: Record<ButtonSize, string> = { sm: '11px', md: '12px', lg: '13px' };

const Surface = styled.button<{
  $kind: ButtonKind;
  $size: ButtonSize;
  $heightOverride?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${(p) => p.theme.space[1.5]};
  height: ${(p) => p.$heightOverride ?? SIZE_HEIGHT[p.$size]};
  padding: 0 ${(p) => SIZE_PADDING_X[p.$size]};
  border-radius: ${(p) => p.theme.radius.lg};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => SIZE_FONT[p.$size]};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  line-height: 1;
  cursor: pointer;
  transition: background ${(p) => p.theme.transition.color};
  border: 1px solid transparent;
  white-space: nowrap;

  ${(p) => {
    const c = p.theme.colors;
    switch (p.$kind) {
      case 'primary':
        return css`
          background: ${c.brand.primary};
          color: #fff;
          border-color: transparent;
          &:hover:not(:disabled) {
            background: ${c.brand.primaryHover};
          }
        `;
      case 'ghost':
        return css`
          background: transparent;
          color: ${c.neutral.inkSoft};
          border-color: transparent;
          &:hover:not(:disabled) {
            background: ${c.neutral.panelAlt};
            color: ${c.neutral.ink};
          }
        `;
      case 'danger':
        return css`
          background: ${c.semantic.error.soft};
          color: ${c.semantic.error.color};
          border-color: ${c.semantic.error.color}33;
          &:hover:not(:disabled) {
            background: ${c.semantic.error.color};
            color: #fff;
          }
        `;
      case 'secondary':
      default:
        return css`
          background: ${c.neutral.panel};
          color: ${c.neutral.ink};
          border-color: ${c.neutral.line};
          &:hover:not(:disabled) {
            border-color: ${c.neutral.lineStrong};
            background: ${c.neutral.panelAlt};
          }
        `;
    }
  }}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${focusRing}

  /* Neutralise MDX auto-paragraph wrapping of text children. && doubles
     specificity so we beat utility classes like Nextra's nx-mt-6. */
  && p {
    margin: 0 !important;
    display: inline;
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
  color: inherit;
`;

/**
 * Generic button primitive with four kinds (primary, secondary, ghost, danger)
 * and three sizes (sm/md/lg = 24/28/34px). Pair with an `icon` for leading
 * glyphs, or render icon-only buttons by passing `aria-label` and omitting
 * `children`.
 *
 * Forwards refs so it can be used as a Radix `asChild` trigger (e.g. inside
 * `<Popover.Trigger>` or `<Tooltip.Trigger>`).
 *
 * Inside a forcing density region the button's `size` prop is **overridden**
 * so its height matches the surrounding controls (`density.controlHeight`):
 *
 * - `<DensityProvider density="filter">` (children of `<FilterBar>`) → 28 px.
 * - `<DensityProvider density="comfortable">` (children of `<Form>`, micro-form
 *   popovers) → 32 px, so action buttons line up with `Select`/`TextInput`
 *   without consumers having to pass `size="lg"`.
 *
 * Compact and spacious densities don't force; the consumer's `size` prop wins.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { kind = 'secondary', size = 'lg', icon, type = 'button', children, ...rest },
  ref,
) {
  const density = useDensity();
  // Forcing regions override BOTH the height and the proportional internals
  // (padding-x, font-size). This keeps buttons looking "right" at the forced
  // height instead of stretched-narrow at the consumer's `size` defaults.
  const forcedSize: ButtonSize | undefined =
    density.name === 'comfortable' ? 'lg' : density.name === 'filter' ? 'md' : undefined;
  const effectiveSize = forcedSize ?? size;
  const heightOverride =
    density.name === 'filter' || density.name === 'comfortable'
      ? `${density.controlHeight}px`
      : undefined;
  return (
    <Surface
      ref={ref}
      $kind={kind}
      $size={effectiveSize}
      $heightOverride={heightOverride}
      type={type}
      {...rest}
    >
      {icon !== undefined && <IconSlot>{icon}</IconSlot>}
      {children}
    </Surface>
  );
});
